import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtWib } from "@/lib/bizdate";
import { rupiah, numberID } from "@/lib/format";
import { Loading, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const NA = <span className="text-slate-400">Tidak tersedia</span>;
export async function downloadPdf(path, fallbackName) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    const cd = r.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    a.download = m ? m[1] : fallbackName || "dokumen.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) { console.error("PDF gagal", e); }
}
const money = (v) => (v == null || v === "" ? <span className="text-amber-600">Belum tersedia</span> : rupiah(v));
const Row = ({ k, v }) => (<div className="flex justify-between gap-4 py-0.5 text-sm"><span className="text-slate-500">{k}</span><span className="text-right font-medium text-slate-800">{v}</span></div>);
const CwBadge = () => <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Catch Weight</span>;
const Section = ({ title, children, testId }) => (<div data-testid={testId}><p className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>{children}</div>);

function History({ hist }) {
  if (!hist || !hist.length) return <p className="text-xs text-slate-400">Tidak tersedia</p>;
  return (
    <ol className="space-y-1">
      {hist.map((h, i) => (
        <li key={i} className="flex gap-2 text-xs"><span className="font-mono text-slate-400">{fmtWib(h.at)}</span><span className="font-semibold text-slate-700">{h.status}</span><span className="text-slate-500">{h.by ? `· oleh ${h.by}` : ""}{h.note ? ` · ${h.note}` : ""}</span></li>
      ))}
    </ol>
  );
}

function Chain({ steps }) {
  const parts = steps.filter((s) => s.label);
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs" data-testid="doc-chain">
      {parts.map((s, i) => (
        <React.Fragment key={i}>
          <span className={`rounded px-2 py-0.5 font-mono font-semibold ${s.active ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}>{s.label}</span>
          {i < parts.length - 1 && <span className="text-slate-400">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function Shell({ title, onClose, loading, children, testId, pdf }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={testId}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        {loading ? <Loading /> : children}
        <DialogFooter>
          {pdf && <Button variant="outline" onClick={() => downloadPdf(pdf)} data-testid={`${testId}-pdf`}>Cetak / Ekspor PDF</Button>}
          <Button variant="outline" onClick={onClose}>Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PRDetail({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/purchasing/purchase-requests/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);
  const rej = (d?.status_history || []).find((h) => ["Ditolak", "Dibatalkan"].includes(h.status));
  const appr = (d?.status_history || []).find((h) => h.status === "Disetujui");
  const cancelH = (d?.status_history || []).find((h) => h.status === "Dibatalkan");
  return (
    <Shell title={`Detail PR · ${d?.pr_no || ""}`} onClose={onClose} loading={!d} testId="pr-detail-dialog" pdf={d ? `/pdf/purchase-request/${id}` : null}>
      {d && (<>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
          <Row k="No PR" v={d.pr_no} /><Row k="Status" v={<StatusBadge status={d.status} />} />
          <Row k="Tanggal" v={d.tanggal} /><Row k="Pemohon / Dibuat Oleh" v={d.pemohon || d.created_by_name || NA} />
          <Row k="Departemen" v={d.departemen || NA} /><Row k="Inventory Tujuan" v={d.warehouse_name || NA} />
          <Row k="Urgensi" v={d.urgensi} />{d.alasan && <Row k="Alasan / Catatan" v={d.alasan} />}
          {d.created_at && <Row k="Dibuat" v={fmtWib(d.created_at)} />}
        </div>
        <Section title="Item" testId="pr-detail-items">
          <div className="rounded-xl border border-slate-200 overflow-hidden"><Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Est. Harga</TableHead><TableHead className="text-right">Est. Subtotal</TableHead></TableRow></TableHeader>
            <TableBody>{d.items.map((it) => { const cw = it.tracking_mode === "CATCH_WEIGHT_ROLL"; return (
              <TableRow key={it.id} data-testid={`pr-detail-line-${it.item_id}`}>
                <TableCell className="text-sm">{it.item_name}{cw && <CwBadge />}{it.category && <span className="block text-[10px] text-slate-400">{it.category}</span>}</TableCell>
                <TableCell className="text-right">{numberID(it.qty)}{cw && it.estimated_weight_kg ? <span className="block text-[10px] text-slate-400">~{numberID(it.estimated_weight_kg)} KG est.</span> : null}</TableCell>
                <TableCell className="text-xs text-slate-500">{it.unit}</TableCell>
                <TableCell className="text-right text-xs">{cw ? (it.estimated_price_per_kg ? `${rupiah(it.estimated_price_per_kg)}/KG` : money(null)) : money(it.estimated_unit_price)}</TableCell>
                <TableCell className="text-right text-xs">{money(it.estimated_total ?? it.estimated_amount ?? it.est_total)}</TableCell>
              </TableRow>); })}</TableBody>
          </Table></div>
          <p className="mt-1 text-right text-sm">Estimasi Total: <b>{money(d.est_grand_total)}</b></p>
        </Section>
        {(appr || rej) && <Section title="Approval" testId="pr-detail-approval">
          {appr && <Row k="Disetujui Oleh" v={`${appr.by || NA} · ${fmtWib(appr.at)}`} />}
          {rej && <><Row k={`${rej.status} Oleh`} v={`${rej.by || NA} · ${fmtWib(rej.at)}`} />{rej.note && <Row k="Alasan" v={rej.note} />}</>}
        </Section>}
        {d.status === "Dibatalkan" && <Section title="Pembatalan PR" testId="pr-detail-cancellation">
          <div className="rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-2">
            <Row k="Dibatalkan Oleh" v={d.cancelled_by || cancelH?.by || NA} />
            <Row k="Waktu Pembatalan (WIB)" v={fmtWib(d.cancelled_at || cancelH?.at)} />
            <Row k="Alasan Pembatalan" v={d.cancel_reason || cancelH?.note || NA} />
            <Row k="Transisi Status" v={<span className="font-mono text-xs"><span className="text-emerald-700">Disetujui</span> <span className="text-slate-400">→</span> <span className="font-semibold text-rose-700">Dibatalkan</span></span>} />
          </div>
        </Section>}
        <Section title="Jejak Dokumen"><Chain steps={[{ label: d.pr_no, active: true }, { label: (d.related_pos || [])[0]?.po_no }]} />
          {(d.related_pos || []).length > 0 && <p className="mt-1 text-xs text-slate-500" data-testid="pr-detail-related-po">PO terkait: {d.related_pos.map((p) => p.po_no).join(", ")}</p>}</Section>
        <Section title="Riwayat / Audit Timeline" testId="pr-detail-timeline"><History hist={d.status_history} /></Section>
      </>)}
    </Shell>
  );
}

export function PODetail({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/purchasing/purchase-orders/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);
  const appr = (d?.status_history || []).find((h) => h.status === "Disetujui");
  const rej = (d?.status_history || []).find((h) => ["Ditolak", "Dibatalkan"].includes(h.status));
  return (
    <Shell title={`Detail PO · ${d?.po_no || ""}`} onClose={onClose} loading={!d} testId="po-detail-dialog" pdf={d ? `/pdf/purchase-order/${id}` : null}>
      {d && (<>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
          <Row k="No PO" v={d.po_no} /><Row k="Status" v={<StatusBadge status={d.status} />} />
          <Row k="Supplier" v={d.supplier_name || NA} /><Row k="Tanggal PO" v={d.tanggal} />
          <Row k="Inventory Tujuan" v={d.warehouse_name || NA} /><Row k="Dibuat Oleh" v={d.created_by_name || NA} />
          {appr && <Row k="Disetujui Oleh" v={`${appr.by || NA} · ${fmtWib(appr.at)}`} />}
          {d.created_at && <Row k="Dibuat" v={fmtWib(d.created_at)} />}
          {d.payment_term && <Row k="Payment Term" v={d.payment_term} />}
          <Row k="Source PR" v={d.pr_ref ? d.pr_ref.pr_no : NA} />
          {d.notes && <Row k="Catatan" v={d.notes} />}
        </div>
        <Section title="Item" testId="po-detail-items">
          <div className="rounded-xl border border-slate-200 overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Ordered</TableHead><TableHead>Basis</TableHead><TableHead className="text-right">Harga</TableHead><TableHead className="text-right">Subtotal</TableHead><TableHead className="text-right">Diterima</TableHead><TableHead className="text-right">Outstanding</TableHead></TableRow></TableHeader>
            <TableBody>{d.items.map((it) => { const cw = it.tracking_mode === "CATCH_WEIGHT_ROLL"; const out = (it.qty_ordered || 0) - (it.qty_received || 0); return (
              <TableRow key={it.id} data-testid={`po-detail-line-${it.item_id}`}>
                <TableCell className="text-sm">{it.item_name}{cw && <CwBadge />}{cw && it.estimated_weight_kg ? <span className="block text-[10px] text-slate-400">{numberID(it.ordered_roll_qty || it.qty_ordered)} Roll · ~{numberID(it.estimated_weight_kg)} KG est.</span> : null}</TableCell>
                <TableCell className="text-right">{numberID(it.qty_ordered)}</TableCell>
                <TableCell className="text-[10px] text-slate-500">{cw ? "Rp/KG" : (it.price_basis || "unit")}</TableCell>
                <TableCell className="text-right text-xs">{cw ? (it.unit_price_per_kg ? `${rupiah(it.unit_price_per_kg)}/KG` : money(null)) : money(it.unit_price)}</TableCell>
                <TableCell className="text-right text-xs">{money(it.subtotal)}</TableCell>
                <TableCell className="text-right">{numberID(it.qty_received || 0)}</TableCell>
                <TableCell className={`text-right font-semibold ${out > 0 ? "text-amber-600" : "text-emerald-600"}`}>{numberID(out)}</TableCell>
              </TableRow>); })}</TableBody>
          </Table></div>
          <p className="mt-1 text-right text-sm">Grand Total: <b>{money(d.grand_total)}</b></p>
        </Section>
        <Section title="Ringkasan Penerimaan" testId="po-detail-receiving">
          {d.items.map((it) => { const out = (it.qty_ordered || 0) - (it.qty_received || 0); return (
            <Row key={it.id} k={it.item_name} v={`${numberID(it.qty_ordered)} ${it.unit} ordered · ${numberID(it.qty_received || 0)} diterima · ${numberID(out)} outstanding`} />); })}
        </Section>
        {rej && <Section title="Pembatalan / Penolakan" testId="po-detail-cancel"><Row k={`${rej.status} Oleh`} v={`${rej.by || NA} · ${fmtWib(rej.at)}`} />{rej.note && <Row k="Alasan" v={rej.note} />}</Section>}
        <Section title="Dokumen Terkait" testId="po-detail-related">
          <Chain steps={[{ label: d.pr_ref?.pr_no }, { label: d.po_no, active: true }, { label: (d.related_grs || [])[0]?.gr_no }, { label: (d.related_bills || [])[0]?.bill_no }, { label: (d.related_payments || [])[0]?.payment_no }]} />
          {(d.related_grs || []).length > 0 && <p className="mt-1 text-xs text-slate-500">GR: {d.related_grs.map((g) => g.gr_no).join(", ")}</p>}
          {(d.related_bills || []).length > 0 && <p className="text-xs text-slate-500">Tagihan: {d.related_bills.map((b) => `${b.bill_no} (${b.status || "-"}, ${rupiah(b.grand_total ?? b.amount)})`).join(", ")}</p>}
          {(d.related_payments || []).length > 0 && <p className="text-xs text-slate-500">Pembayaran: {d.related_payments.map((p) => `${p.payment_no} (${rupiah(p.amount)})`).join(", ")}</p>}
        </Section>
        <Section title="Riwayat / Audit Timeline" testId="po-detail-timeline"><History hist={d.status_history} /></Section>
      </>)}
    </Shell>
  );
}

export function GRDetail({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/purchasing/goods-receipts/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);
  return (
    <Shell title={`Detail GR · ${d?.gr_no || ""}`} onClose={onClose} loading={!d} testId="gr-detail-dialog" pdf={d ? `/pdf/goods-receipt/${id}` : null}>
      {d && (<>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
          <Row k="No GR" v={d.gr_no} /><Row k="PO Source" v={d.po_no || d.po_ref?.po_no || NA} />
          <Row k="Supplier" v={d.supplier_name || NA} /><Row k="Tanggal" v={d.tanggal} />
          <Row k="Warehouse Tujuan" v={d.warehouse_name || NA} /><Row k="Diterima Oleh" v={d.penerima || d.created_by_name || NA} />
          {d.created_at && <Row k="Dibuat" v={fmtWib(d.created_at)} />}{d.notes && <Row k="Catatan" v={d.notes} />}
        </div>
        <Section title="Item" testId="gr-detail-items">
          <div className="rounded-xl border border-slate-200 overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty Diterima</TableHead><TableHead className="text-right">Ditolak</TableHead><TableHead>Unit</TableHead><TableHead className="text-right">Variance KG</TableHead></TableRow></TableHeader>
            <TableBody>{d.items.map((it) => { const cw = it.tracking_mode === "CATCH_WEIGHT_ROLL"; return (
              <React.Fragment key={it.id}>
                <TableRow data-testid={`gr-detail-line-${it.item_id}`}>
                  <TableCell className="text-sm">{it.item_name}{cw && <CwBadge />}</TableCell>
                  <TableCell className="text-right">{numberID(it.qty_received)}{cw && it.actual_received_weight_kg != null ? <span className="block text-[10px] text-emerald-600">{numberID(it.actual_received_weight_kg)} KG actual</span> : null}</TableCell>
                  <TableCell className="text-right">{numberID(it.qty_rejected || 0)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{it.unit}</TableCell>
                  <TableCell className="text-right text-xs">{it.weight_variance_kg != null ? `${numberID(it.weight_variance_kg)} KG` : "-"}</TableCell>
                </TableRow>
                {cw && (it.rolls || []).length > 0 && (
                  <TableRow data-testid={`gr-detail-rolls-${it.item_id}`}><TableCell colSpan={5} className="bg-amber-50/40">
                    <p className="mb-1 text-[11px] font-semibold text-amber-700">Detail Roll (actual weight — bukan estimasi PO)</p>
                    {it.rolls.map((r, ri) => (<div key={ri} className="flex justify-between text-[11px] text-slate-600"><span className="font-mono">{r.roll_no || r.roll_id || `Roll ${ri + 1}`}</span><span>{numberID(r.actual_weight_g != null ? r.actual_weight_g / 1000 : (r.weight_kg ?? r.received_weight_kg))} KG{r.remaining_weight_g != null ? ` · sisa ${numberID(r.remaining_weight_g / 1000)} KG` : ""} · {r.price_per_kg ? `${rupiah(r.price_per_kg)}/KG` : (it.unit_price_per_kg ? `${rupiah(it.unit_price_per_kg)}/KG` : "")}{r.status ? ` · ${r.status}` : ""}</span></div>))}
                    <p className="mt-1 text-right text-[11px] font-semibold">Total: {it.received_roll_qty || it.rolls.length} Roll · {numberID(it.actual_received_weight_kg)} KG actual{it.actual_receipt_value ? ` · ${rupiah(it.actual_receipt_value)}` : ""}</p>
                  </TableCell></TableRow>
                )}
              </React.Fragment>); })}</TableBody>
          </Table></div>
        </Section>
        <Section title="Dokumen Terkait"><Chain steps={[{ label: d.po_ref?.po_no || d.po_no }, { label: d.gr_no, active: true }]} />
          {(d.inventory_txn_refs || []).length > 0 && <p className="mt-1 text-xs text-slate-500" data-testid="gr-detail-inv-txn">Inventory Txn: {d.inventory_txn_refs.map((t) => t.kind || t.id).join(", ")}</p>}</Section>
      </>)}
    </Shell>
  );
}
